import cn from "classnames";
import moment from "moment";
import React, {useEffect, useState} from "react";
import DatePicker from "react-datepicker";
import Switch from "react-switch";
import Select, {components} from "react-select";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import _ from "lodash";
import numeral from "numeral";
import equal from "deep-equal";
import {
    createCampaign as createCampaignApi,
    getBranch as getBranchApi,
    getCampaignByCountryCode as getCampaignByCountryCodeApi,
    getCountry as getCountryApi,
    getExchangeRateByCountryCode as getExchangeRateByCountryCodeApi,
    updateCampaign as updateCampaignApi,
    updateSplitFunctionConfig as updateSplitFunctionConfigApi,
} from "../../api/IrsClient";
import Modal from "../common/Modal";
import ModalError from "../common/ModalError";
import Loader from "../common/Loader";
import { BASE_10 } from "../../constant";

var m = moment().utcOffset(0);
m.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

const TierPricing = ({ authentication, userGroup }) => {
  const [mode, setMode] = useState("VIEW");
  const [isSubmitPopupShown, setSubmitPopupShown] = useState(false);
  const [isWarningPopupShown, setWarningPopupShown] = useState(false);
  const [isDeleteTierPopupShown, setDeleteTierPopupShown] = useState(false);
  const [isResetToDefaultPopupShown, setResetToDefaultPopupShown] =
    useState(false);
  const [isSplitFunctionPopupShown, setSplitFunctionPopupShown] =
    useState(false);
  const [isActivatedSplitFunction, setActivatedSplitFunction] = useState(false);
  const [rate, setRate] = useState(0); // MMK
  const [selectedCurrency, setSelectedCurrency] = useState("MMK");
  const [country, setCountry] = useState([]);
  const [currency, setCurrency] = useState(null);
  const [branch, setBranch] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [defaultValue, setDefaultValue] = useState(null);

  const initialValue = {
    country: "95",
    branch: [
      {
        label: "All",
        value: "ALL",
      },
    ],
    currency: "MMK",
    date: {
      from: m.valueOf(),
      to: m.valueOf(),
    },
    tier: [],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    control,
    reset,
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: initialValue,
  });

  useEffect(() => {
    setFormDefaultValue();
  }, [rate, campaign]);

  useEffect(() => {
    init();
  }, [authentication]);

  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: "tier", // unique name for your Field Array
  });

  function setFormDefaultValue() {
    let value = initialValue;
    setDefaultValue(value);
  }

  async function init() {
    try {
      let tmpBranchData = [];
      setLoading(true);
      const [branchRes, countryRes] = await Promise.all([
        getBranchApi(),
        getCountryApi(),
      ]);
      if (branchRes.data.status.code === "0") {
        tmpBranchData = branchRes.data.data.branch_list.map((x) => ({
          value: x.account,
          label: x.account,
        }));
        const branchValues = [{ value: "ALL", label: "All" }, ...tmpBranchData];
        setBranch(branchValues);
      }

      if (countryRes.data.status.code === "0") {
        const country = countryRes.data.data.country_list;
        setCountry(country);
        if (!country.length) return;
        const [exchangeRateRes, campaignRes] = await Promise.all([
          getExchangeRateByCountryCodeApi(country[0].country_code),
          getCampaignByCountryCodeApi(country[0].country_code),
        ]);

        let tmpRate = 0;

        if (exchangeRateRes.data.status.code === "0") {
          const exchangeRate = 1 / exchangeRateRes.data.data.rate;
          const currencyUnit = exchangeRateRes.data.data.currency_unit;
          tmpRate = exchangeRate;
          setRate(exchangeRate);
          setCurrency(currencyUnit);
          setValue("currency", currencyUnit);
        }

        if (campaignRes.data.status.code === "0") {
          const isEnabledSplitFunction =
            campaignRes.data.data.split_func_enabled;
          let tmpCampaign = campaignRes.data.data.campaign_list.length
            ? campaignRes.data.data.campaign_list[0]
            : null;
          setActivatedSplitFunction(isEnabledSplitFunction === "true");
          if (tmpCampaign) {
            tmpCampaign = {
              ...tmpCampaign,
              tier_list: _.orderBy(
                tmpCampaign.tier_list,
                ["tier_level"],
                ["asc"]
              ),
            };
            const tmpBranch = tmpCampaign.branch_list.map((x) => ({
              value: x,
              label: x,
            }));
            setSelectedCurrency(tmpCampaign.currency_code);
            setCampaign(tmpCampaign);
            setValue("country", tmpCampaign.country_code);
            setValue(
              "branch",
              tmpBranchData.length === tmpCampaign.branch_list.length
                ? [{ value: "ALL", label: "All" }, ...tmpBranch]
                : tmpBranch
            );
            setValue("date.from", moment(tmpCampaign.start_date).valueOf());
            setValue("date.to", moment(tmpCampaign.end_date).valueOf());
            setValue("currency", tmpCampaign.currency_code);

            tmpCampaign.tier_list.forEach((x, i) => {
              const fromOrigin =
                tmpCampaign.currency_code !== "THB"
                  ? x.min_amount
                  : (Math.round((x.min_amount / tmpRate) * 100) / 100).toFixed(
                      2
                    );
              const fromDestination =
                tmpCampaign.currency_code === "THB"
                  ? x.min_amount.toFixed(2)
                  : (Math.round(x.min_amount * tmpRate * 100) / 100).toFixed(2);
              const toOrigin =
                tmpCampaign.currency_code !== "THB"
                  ? x.max_amount
                  : (
                      Math.round((parseFloat(x.max_amount) / tmpRate) * 100) /
                      100
                    ).toFixed(2);
              const toDestination =
                tmpCampaign.currency_code === "THB"
                  ? x.max_amount.toFixed(2)
                  : (
                      Math.round(parseFloat(x.max_amount) * tmpRate * 100) / 100
                    ).toFixed(2);
              append({
                cashback: x.cashback_amount,
                from_origin: fromOrigin,
                from_destination: fromDestination,
                to_origin: toOrigin,
                to_destination: toDestination,
              });
            });
          } else {
            setWarningPopupShown(true);
            append({
              cashback: 0,
              from_origin: 1,
              from_destination: (Math.round(tmpRate * 100) / 100).toFixed(2),
              to_origin: undefined,
              to_destination: undefined,
            });
          }
        } else {
          setWarningPopupShown(true);
          append({
            cashback: 0,
            from_origin: 1,
            from_destination: (Math.round(tmpRate * 100) / 100).toFixed(2),
            to_origin: undefined,
            to_destination: undefined,
          });
        }
      }
    } catch (error) {
      console.log("Error while init()", error);
    }
    setLoading(false);
  }

  async function initCampaignByCountryCode(countryCode) {
    try {
      remove();
      setLoading(true);
      const [exchangeRateRes, campaignRes] = await Promise.all([
        getExchangeRateByCountryCodeApi(countryCode),
        getCampaignByCountryCodeApi(countryCode),
      ]);

      let tmpRate = 0;

      if (exchangeRateRes.data.status.code === "0") {
        const exchangeRate = 1 / exchangeRateRes.data.data.rate;
        const currencyUnit = exchangeRateRes.data.data.currency_unit;
        tmpRate = exchangeRate;
        setRate(exchangeRate);
        setCurrency(currencyUnit);
        setValue("currency", currencyUnit);
      }

      if (campaignRes.data.status.code === "0") {
        const isEnabledSplitFunction = campaignRes.data.data.split_func_enabled;
        let tmpCampaign = campaignRes.data.data.campaign_list.length
          ? campaignRes.data.data.campaign_list[0]
          : null;
        setActivatedSplitFunction(isEnabledSplitFunction === "true");
        if (tmpCampaign) {
          tmpCampaign = {
            ...tmpCampaign,
            tier_list: _.orderBy(
              tmpCampaign.tier_list,
              ["tier_level"],
              ["asc"]
            ),
          };
          setSelectedCurrency(tmpCampaign.currency_code);
          setCampaign(tmpCampaign);
          setValue("country", tmpCampaign.country_code);
          setValue(
            "branch",
            branch.length === tmpCampaign.branch_list.length
              ? [{ value: "ALL", label: "Select All" }]
              : tmpCampaign.branch_list.map((x) => ({
                  value: x,
                  label: x,
                }))
          );
          setValue("date.from", moment(tmpCampaign.start_date).valueOf());
          setValue("date.to", moment(tmpCampaign.end_date).valueOf());
          setValue("currency", tmpCampaign.currency_code);

          tmpCampaign.tier_list.forEach((x, i) => {
            const fromOrigin =
              tmpCampaign.currency_code !== "THB"
                ? x.min_amount
                : (Math.round((x.min_amount / tmpRate) * 100) / 100).toFixed(2);
            const fromDestination =
              tmpCampaign.currency_code === "THB"
                ? x.min_amount.toFixed(2)
                : (Math.round(x.min_amount * tmpRate * 100) / 100).toFixed(2);
            const toOrigin =
              tmpCampaign.currency_code !== "THB"
                ? x.max_amount
                : (
                    Math.round((parseFloat(x.max_amount) / tmpRate) * 100) / 100
                  ).toFixed(2);
            const toDestination =
              tmpCampaign.currency_code === "THB"
                ? x.max_amount.toFixed(2)
                : (
                    Math.round(parseFloat(x.max_amount) * tmpRate * 100) / 100
                  ).toFixed(2);
            append({
              cashback: x.cashback_amount,
              from_origin: fromOrigin,
              from_destination: fromDestination,
              to_origin: toOrigin,
              to_destination: toDestination,
            });
          });
        } else {
          setWarningPopupShown(true);
          append({
            cashback: 0,
            from_origin: 1,
            from_destination: (Math.round(tmpRate * 100) / 100).toFixed(2),
            to_origin: undefined,
            to_destination: undefined,
          });
        }
      } else {
        setWarningPopupShown(true);
        append({
          cashback: 0,
          from_origin: 1,
          from_destination: (Math.round(tmpRate * 100) / 100).toFixed(2),
          to_origin: undefined,
          to_destination: undefined,
        });
      }
    } catch (error) {
      console.log("Error while initCampaignByCountryCode()", error);
    }
    setLoading(false);
  }

  function onSubmit() {
    setSubmitPopupShown(true);
  }

  async function submitCampaign() {
    try {
      setLoading(true);
      const values = getValues();
      const data = {
        country_code: values.country,
        currency_unit: values.currency,
        start_date: moment(values.date.from).format("YYYY-MM-DD"),
        end_date: moment(values.date.to).format("YYYY-MM-DD"),
        tier_list: values.tier.map((x, i) => ({
          tier_level: i + 1,
          min_amount:
            values.currency === "THB"
              ? parseFloat(x.from_destination)
              : parseFloat(x.from_origin),
          max_amount:
            values.currency === "THB"
              ? parseFloat(x.to_destination)
              : parseFloat(x.to_origin),
          cashback_amount: parseInt(x.cashback, BASE_10),
        })),
        branch_list: values.branch.find((x) => x.value === "ALL")
          ? branch.filter((x) => x.value !== "ALL").map((x) => x.value)
          : values.branch.filter((x) => x.value !== "ALL").map((x) => x.value),
      };
      // update

      let res = null;
      if (campaign) {
        // compare between previous data and current form data, ir should not update when data is not change.
        if (compareValue(campaign, data)) {
          setSubmitPopupShown(false);
          setMode("VIEW");
          setLoading(false);
          return;
        }
        res = await updateCampaignApi(campaign.campaign_id, data);
      } else {
        res = await createCampaignApi(data);
      }

      // if error
      if (res.data.status.code !== "0") {
        setError(res.data.status.message);
      }

      setSubmitPopupShown(false);
      setMode("VIEW");
      setSelectedCurrency("MMK");
      reset(defaultValue);
      init();
    } catch (error) {
      console.log("Error while save campaign", error);
      if (error?.response?.data?.status?.code) {
        setError(
          error?.response?.data?.status?.code?.message ??
            "Something went wrong."
        );
      }
      setLoading(false);
    }
  }

  function compareValue(campaign, formData) {
    let tmpCampaign = { ...campaign, currency_unit: campaign.currency_code };
    delete tmpCampaign.campaign_id;
    delete tmpCampaign.currency_code;
    tmpCampaign = {
      ...tmpCampaign,
      branch_list: _.sortBy(tmpCampaign.branch_list),
      tier_list: tmpCampaign.tier_list.map((x) => ({
        ...x,
        min_amount: x.min_amount.toFixed(2),
        max_amount: x.max_amount.toFixed(2),
      })),
    };
    formData = {
      ...formData,
      branch_list: _.sortBy(formData.branch_list),
      tier_list: formData.tier_list.map((x) => ({
        ...x,
        min_amount: x.min_amount.toFixed(2),
        max_amount: x.max_amount.toFixed(2),
      })),
    };
    return equal(tmpCampaign, formData);
  }

  async function updateSplitFunction() {
    try {
      setLoading(true);
      const res = await updateSplitFunctionConfigApi();
      // if error
      if (res.data.status.code !== "0") {
        setError(res.data.status.message);
      } else {
        setActivatedSplitFunction((prevState) => !prevState);
      }
    } catch (error) {
      if (error?.response?.data?.status?.code) {
        setError(
          error?.response?.data?.status?.message ?? "Something went wrong."
        );
      }
    }

    setLoading(false);
  }

  function restore() {
    const currencyUnit = campaign?.currency_code ?? "MMK";
    setSelectedCurrency(currencyUnit);
    remove();
    reset({ ...defaultValue, currency: currencyUnit, tier: [] });
    // delay for remove previous tier list fields
    setTimeout(() => {
      append({
        cashback: 0,
        from_origin: 1,
        from_destination: (Math.round(rate * 100) / 100).toFixed(2),
        to_origin: undefined,
        to_destination: undefined,
      });
    }, 100);
  }

  function handleChangeCurrency(e) {
    const currency = e.target.value;
    setSelectedCurrency(currency);
    setValue(
      "tier.0.from_origin",
      currency === "MMK" ? 1 : (Math.round((1 / rate) * 100) / 100).toFixed(2)
    );
    setValue(
      "tier.0.from_destination",
      currency === "MMK" ? (Math.round(rate * 100) / 100).toFixed(2) : 1
    );
    setValue("tier.0.to_origin", undefined);
    setValue("tier.0.to_destination", undefined);
    remove(new Array(fields.length).fill(0).map((_, i) => i + 1));
  }

  async function addTier(e) {
    e.preventDefault();
    const fieldsLength = fields.length;
    const values = getValues();

    let fromOriginInitialValue = 0;
    let fromDestinationInitialValue = 0;
    if (selectedCurrency === "MMK") {
      fromOriginInitialValue =
        parseInt(values.tier[fieldsLength - 1].to_origin, BASE_10) + 1;
      fromDestinationInitialValue = (
        Math.round(fromOriginInitialValue * rate * 100) / 100
      ).toFixed(2);
    } else {
      const prevFromDestinationValue =
        values.tier[fieldsLength - 1].to_destination;
      fromDestinationInitialValue = (
        parseFloat(prevFromDestinationValue) + 0.01
      ).toFixed(2);
      fromOriginInitialValue = (
        Math.round((parseFloat(fromDestinationInitialValue) / rate) * 100) / 100
      ).toFixed(2);
    }

    if (fields.length >= 15) return;
    const validate = await trigger();
    if (!validate) return;

    append({
      cashback: 0,
      from_origin: fromOriginInitialValue,
      from_destination: fromDestinationInitialValue,
      to_origin: undefined,
      to_destination: undefined,
    });
  }

  function handleChangeCountry(e) {
    const countryCode = e.target.value;
    initCampaignByCountryCode(countryCode);
  }

  async function handleChangeInputCashback(e, currentTierIndex) {
    if (e.key === "Enter") {
      return;
    }
    let cashBackValue = e.target.value;

    // check start with 1-9
    if (!new RegExp(/^[1-9][0-9.]*$/).test(cashBackValue)) {
      while (cashBackValue.charAt(0) === "0") {
        cashBackValue = cashBackValue.substr(1);
      }
    }

    cashBackValue = cashBackValue ? cashBackValue.replace(/[^\d]+/g, "") : 0;

    setValue(`tier.${currentTierIndex}.cashback`, cashBackValue);
    await trigger();
  }

  async function handleChangeInputToOrigin(e, currentTierIndex) {
    if (e.key === "Enter") {
      return;
    }

    let toOriginValue = e.target.value;

    // check start with 1-9
    if (!new RegExp(/^[1-9][0-9.]*$/).test(toOriginValue)) {
      while (toOriginValue.charAt(0) === "0") {
        toOriginValue = toOriginValue.substr(1);
      }
    }

    toOriginValue = toOriginValue
      ? toOriginValue.replace(/[^\d]+/g, "")
      : undefined;

    setValue(`tier.${currentTierIndex}.to_origin`, toOriginValue);

    toOriginValue = parseFloat(toOriginValue);

    setValue(
      `tier.${currentTierIndex}.to_destination`,
      isNaN(toOriginValue)
        ? undefined
        : (Math.round(toOriginValue * rate * 100) / 100).toFixed(2)
    );

    await trigger();

    // populate next tier data
    const nextTier = fields.slice(currentTierIndex + 1);

    for (let i in nextTier) {
      let tmpFromOriginValue = undefined;
      let tmpFromDestinationValue = undefined;
      i = parseInt(i, BASE_10);

      if (i === 0) {
        tmpFromOriginValue = !isNaN(toOriginValue)
          ? parseInt(toOriginValue, BASE_10) + 1
          : undefined;
        tmpFromDestinationValue = !isNaN(toOriginValue)
          ? (Math.round(tmpFromOriginValue * rate * 100) / 100).toFixed(2)
          : undefined;
      } else {
        const prevValue = parseInt(
          getValues(`tier.${i + currentTierIndex}.to_origin`),
          BASE_10
        );
        tmpFromOriginValue = isNaN(prevValue) ? undefined : prevValue + 1;
        tmpFromDestinationValue = isNaN(prevValue)
          ? undefined
          : (Math.round(tmpFromOriginValue * rate * 100) / 100).toFixed(2);
      }

      setValue(
        `tier.${i + currentTierIndex + 1}.from_origin`,
        tmpFromOriginValue
      );
      setValue(
        `tier.${i + currentTierIndex + 1}.from_destination`,
        tmpFromDestinationValue
      );
    }
  }

  function validateInputToOrigin(value, currentTierIndex) {
    const toOriginValue = parseInt(value, BASE_10);
    const fromOriginValue = parseInt(
      getValues(`tier.${currentTierIndex}.from_origin`),
      BASE_10
    );

    if (toOriginValue <= fromOriginValue) return "The value is incorrect";

    return true;
  }

  async function handleChangeInputToDestination(e, currentTierIndex) {
    if (e.key === "Enter") {
      return;
    }
    let toDestinationValue = e.target.value;

    // check start with 1-9
    if (!new RegExp(/^[1-9][0-9]*$/).test(toDestinationValue)) {
      while (toDestinationValue.charAt(0) === "0") {
        toDestinationValue = toDestinationValue.substr(1);
      }
    }

    // check decimal more than 3 digits
    if (
      toDestinationValue &&
      !new RegExp(/^\s*(?=.*[1-9])\d*(?:\.\d{1,2})?\s*$/).test(
        toDestinationValue
      )
    ) {
      toDestinationValue = toDestinationValue.slice(0, -1);
    }

    setValue(
      `tier.${currentTierIndex}.to_origin`,
      isNaN(toDestinationValue) || !toDestinationValue
        ? undefined
        : (
            Math.round((parseFloat(toDestinationValue) / rate) * 100) / 100
          ).toFixed(2)
    );
    setValue(
      `tier.${currentTierIndex}.to_destination`,
      isNaN(toDestinationValue) || !toDestinationValue
        ? undefined
        : !new RegExp(/^\s*-?[1-9]\d*(\.\d{1,2})?\s*$/).test(toDestinationValue)
        ? parseFloat(toDestinationValue).toFixed(2)
        : toDestinationValue
    );

    await trigger();

    // populate next tier data

    const nextTier = fields.slice(currentTierIndex + 1);

    for (let i in nextTier) {
      let tmpFromOriginValue = undefined;
      let tmpFromDestinationValue = undefined;
      i = parseInt(i, BASE_10);
      if (i === 0) {
        tmpFromDestinationValue = isNaN(toDestinationValue)
          ? undefined
          : (parseFloat(toDestinationValue) + 0.01).toFixed(2);

        tmpFromOriginValue = isNaN(toDestinationValue)
          ? undefined
          : Math.round((parseFloat(tmpFromDestinationValue) / rate) * 100) /
            100;
      } else {
        const prevValue = parseFloat(
          getValues(`tier.${i + currentTierIndex}.to_destination`)
        );
        tmpFromDestinationValue = isNaN(prevValue)
          ? undefined
          : (prevValue + 0.01).toFixed(2);
        tmpFromOriginValue = isNaN(prevValue)
          ? undefined
          : Math.round((parseFloat(tmpFromDestinationValue) / rate) * 100) /
            100;
      }

      setValue(
        `tier.${i + currentTierIndex + 1}.from_origin`,
        tmpFromOriginValue
      );
      setValue(
        `tier.${i + currentTierIndex + 1}.from_destination`,
        tmpFromDestinationValue
      );
    }
  }

  function onChangeDate(name, date) {
    setValue(name, date);
  }

  function validateInputToDestination(value, currentTierIndex) {
    const toDestinationValue = parseFloat(value);
    const fromDestinationValue = parseFloat(
      getValues(`tier.${currentTierIndex}.from_destination`)
    );

    if (toDestinationValue <= fromDestinationValue)
      return "The value is incorrect";

    return true;
  }

  function validateDateTo(valueDateTo) {
    const date = moment(valueDateTo).valueOf();
    let dateToCompare = getValues("date.from");
    if (!dateToCompare) return true;
    dateToCompare = moment(dateToCompare);
    if (date < dateToCompare.valueOf()) {
      return "Should more than date from";
    }
    const endOfmonth = moment(dateToCompare).endOf("month").valueOf();
    if (date > endOfmonth) {
      return "Range is no longer than end of month";
    }
    return true;
  }

  function validateDateFrom(value) {
    const date = moment(value).valueOf();
    let dateToCompare = getValues("date.to");
    if (!dateToCompare) return true;
    dateToCompare = moment(dateToCompare).valueOf();
    if (date > dateToCompare) {
      return "Should less than date to";
    }
    return true;
  }

  function validateCashback(currentTierCashback, currentTierIndex) {
    if (currentTierIndex === 0) return true;
    const prevTierIndex = currentTierIndex - 1;
    const prevTierCashback = getValues(`tier.${prevTierIndex}.cashback`);
    if (+currentTierCashback <= +prevTierCashback) {
      return "The value is incorrect";
    }
    return true;
  }

  function validateBranch(value) {
    if (value.length) return true;
    return "Please select branch";
  }

  function onConfirmDeleteTier() {
    const lastFieldsIndex = fields.length - 1;
    remove(lastFieldsIndex);
    setDeleteTierPopupShown(false);
  }

  function onConfirmResetToDefault() {
    restore();
    setResetToDefaultPopupShown(false);
  }

  function onConfirmSplitFunction() {
    setSplitFunctionPopupShown(false);
    updateSplitFunction();
  }

  function renderCurrency(amount, forceDecimalShown) {
    let format =
      campaign && campaign.currency_code === "THB" ? "0,0.00" : "0,0";
    if (forceDecimalShown) {
      format = "0,0.00";
    }
    return numeral(amount).format(format) || numeral(0).format(format);
  }

  const reactSelectCustomStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#80bdff" : "hsl(0,0%,80%)",
      outline: "0",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "unset",
    }),
    valueContainer: (provided) => ({
      ...provided,
    }),
    multiValue: (provided, state) => {
      return state.data.value === "ALL"
        ? { ...provided, paddingRight: "6px" }
        : provided;
    },
    multiValueRemove: (provided, state) => {
      return state.data.value === "ALL"
        ? { ...provided, display: "none" }
        : provided;
    },
  };

  function MultiValue({ children, ...props }) {
    const selectedBranch = getValues(props.selectProps.name);
    if (!selectedBranch) {
      return null;
    }
    const x = selectedBranch.find((x) => x.value === "ALL");
    if (x) {
      if (children === "All") {
        return (
          <components.MultiValue {...props}>{children}</components.MultiValue>
        );
      } else {
        return null;
      }
    }
    return <components.MultiValue {...props}>{children}</components.MultiValue>;
  }

  function handleSelectChange(key, values, newValue) {
    if (newValue.option && newValue.option.value === "ALL") {
      setValue(key, [newValue.option]);
    } else {
      if (!values || values.length === 0) {
        setValue(key, [
          {
            value: "ALL",
            label: "All",
          },
        ]);
      } else {
        setValue(
          key,
          values.filter((x) => x.value !== "ALL")
        );
      }
    }
  }

  // use for re-render date to
  watch("date.from", "date.to");

  return (
    <div className="container">
      <Loader isLoading={isLoading} />
      <Modal
        data-testid="submit-campaign"
        message="Submit the following data?"
        isOpen={isSubmitPopupShown}
        onCancel={() => setSubmitPopupShown(false)}
        onOk={() => submitCampaign()}
      />
      <Modal
        data-testid="reset-data"
        message="Reset to default?"
        textOk="Reset"
        isOpen={isResetToDefaultPopupShown}
        onCancel={() => setResetToDefaultPopupShown(false)}
        onOk={onConfirmResetToDefault}
      />
      <Modal
        message="Delete this tier?"
        textOk="Delete"
        isOpen={isDeleteTierPopupShown}
        onCancel={() => setDeleteTierPopupShown(false)}
        onOk={onConfirmDeleteTier}
      />

      <Modal
        message={`${
          isActivatedSplitFunction ? "Turn off" : "Turn on"
        } split function?`}
        isOpen={isSplitFunctionPopupShown}
        onCancel={() => setSplitFunctionPopupShown(false)}
        onOk={onConfirmSplitFunction}
      />

      <ModalError
        message="No Active Campaign"
        isOpen={isWarningPopupShown}
        onRequestClose={() => setWarningPopupShown(false)}
      />

      <ModalError
        isOpen={error !== ""}
        onRequestClose={() => setError("")}
        message={error}
      />

      <div className="card card-secondary">
        <div className="card-header">
          <h5>IRS Tier Pricing</h5>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="p-4">
            {userGroup.includes("/services/irs/irs-administrator") && (
              <div className="row">
                <div className="col-md-12 text-right">
                  <button
                    data-testid="reset-tier"
                    className="btn-tier-pricing btn btn-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      setResetToDefaultPopupShown(true);
                    }}
                    disabled={mode === "VIEW"}
                  >
                    Reset to default
                  </button>
                  {mode === "VIEW" ? (
                    <button data-testid="edit-tier"
                      className="btn-tier-pricing btn btn-warning ml-2"
                      type="submit"
                      onClick={(e) => {
                        e.preventDefault();
                        setMode("EDIT");
                      }}
                    >
                      Edit
                    </button>
                  ) : (
                    <button
                      data-testid="submit-tier"
                      className="btn-tier-pricing btn btn-warning ml-2"
                      type="submit"
                    >
                      Submit
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-12 py-4">
                <h5>Admin Tier Pricing</h5>
                <div className="divider" />
                <div className="row mt-3 mx-0 px-4">
                  <div className="col-sm-4">
                    <div className="form-group mb-0 row">
                      <label className="col-form-label col-sm-5 text-lg-right">
                        Country
                      </label>
                      <div className="col-sm-7 p-0">
                        <fieldset disabled>
                          <select
                            className="form-control"
                            {...register("country", { required: true })}
                            onChange={handleChangeCountry}
                          >
                            {country.map((x) => (
                              <option
                                key={x.country_code}
                                value={x.country_code}
                              >
                                {x.country_name}
                              </option>
                            ))}
                          </select>
                        </fieldset>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="form-group mb-0 row">
                      <label className="col-form-label col-sm-5 text-lg-right" htmlFor="branch-tier" >
                        Branch
                      </label>
                      <div className="col-sm-7 p-0">
                        <Controller
                          render={({ field }) => (
                            <Select
                              inputId="branch-tier"
                              {...field}
                              isMulti={true}
                              isDisabled={mode === "VIEW"}
                              closeMenuOnSelect={false}
                              isSearchable
                              options={branch}
                              hideSelectedOptions={false}
                              styles={reactSelectCustomStyles}
                              components={{ MultiValue }}
                              onChange={(values, newValue) =>
                                handleSelectChange("branch", values, newValue)
                              }
                            />
                          )}
                          name="branch"
                          control={control}
                          rules={{
                            validate: (value) => validateBranch(value),
                          }}
                          defaultValue={[
                            {
                              value: "ALL",
                              label: "All",
                            },
                          ]}
                        />
                        {errors?.branch?.type === "validate" && (
                          <span className="input-error-message">
                            {errors?.branch?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {userGroup.includes("/services/irs/irs-administrator") && (
                    <div className="col-sm-4 form-switch">
                      <span className="mr-3">Split Function</span>
                      <Switch
                        onChange={() => setSplitFunctionPopupShown(true)}
                        checked={isActivatedSplitFunction}
                        uncheckedIcon={null}
                        checkedIcon={null}
                        onColor="#144062"
                      />
                    </div>
                  )}
                </div>

                <div className="row mt-3 mx-0 px-4">
                  <div className="col-sm-4">
                    <div
                      className={cn("form-group mb-0 row", {
                        "form-group-error": errors?.date?.from,
                      })}
                    >
                      <label className="col-form-label col-sm-5 text-lg-right">
                        From
                      </label>
                      <div className="col-sm-7 p-0">
                        <Controller
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              className="form-control"
                              allowSameDay
                              minDate={new Date()}
                              dateFormat="dd/MM/yyyy"
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              disabled={mode === "VIEW"}
                              onChange={(date) =>
                                onChangeDate("date.from", date.valueOf())
                              }
                              selected={getValues("date.from")}
                            />
                          )}
                          control={control}
                          name="date.from"
                          rules={{
                            required: true,
                            validate: (value) => validateDateFrom(value),
                          }}
                        />
                        {errors?.date?.from?.type === "required" && (
                          <span className="input-error-message">
                            Please select date
                          </span>
                        )}
                        {errors?.date?.from?.type === "validate" && (
                          <span className="input-error-message">
                            {errors?.date?.from?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div
                      className={cn("form-group mb-0 row", {
                        "form-group-error": errors?.date?.to,
                      })}
                    >
                      <label className="col-form-label col-sm-5 text-lg-right">
                        To
                      </label>
                      <div className="col-sm-7 p-0">
                        <Controller
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              className="form-control"
                              allowSameDay
                              minDate={getValues("date.from")}
                              maxDate={moment(getValues("date.from"))
                                .endOf("month")
                                .valueOf()}
                              dateFormat="dd/MM/yyyy"
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              disabled={mode === "VIEW"}
                              onChange={(date) =>
                                onChangeDate("date.to", date.valueOf())
                              }
                              selected={getValues("date.to")}
                            />
                          )}
                          control={control}
                          name="date.to"
                          rules={{
                            required: true,
                            validate: (value) => validateDateTo(value),
                          }}
                        />
                        {errors?.date?.to?.type === "required" && (
                          <span className="input-error-message">
                            Please select date
                          </span>
                        )}
                        {errors?.date?.to?.type === "validate" && (
                          <span className="input-error-message">
                            {errors?.date?.to?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div
                      className={cn("form-group mb-0 row", {
                        "form-group-error": errors?.currency,
                      })}
                    >
                      <label className="col-form-label col-sm-5 text-lg-right">
                        Currency
                      </label>
                      <div className="col-sm-7 p-0">
                        <fieldset disabled={mode === "VIEW" || fields.length > 1}>
                          <select
                            data-testid="currency"
                            {...register("currency", { required: true })}
                            className="form-control"
                            onChange={handleChangeCurrency}
                          >
                            {currency && (
                              <option value={currency}>{currency}</option>
                            )}
                            <option value="THB">THB</option>
                          </select>
                        </fieldset>
                        {errors?.currency?.type === "required" && (
                          <span className="input-error-message">
                            Please select currency
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 py-4">
                <h5>Tier Adjustment</h5>
              </div>

              {fields.map((field, index) => {
                return (
                  <div key={field.id} className="col-md-12">
                    <div className="card card-shadow p-4 mb-4">
                      {fields.length > 1 &&
                        fields.length === index + 1 &&
                        mode === "EDIT" && (
                          <span
                            className="tier-button-warning"
                            onClick={() => setDeleteTierPopupShown(true)}
                          >
                            Delete Tier
                          </span>
                        )}
                      <h5>Tier {index + 1}</h5>
                      <div className="row mt-3 mx-0">
                        <div className="col-sm-4">
                          <div className="form-group mb-0 row">
                            <label className="col-form-label col-sm-5 text-lg-right">
                              From
                            </label>
                            <div className="col-sm-7 p-0">
                              <div className="input-group">
                                {mode === "EDIT" ? (
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(`tier.${index}.from_origin`, {
                                      required: true,
                                    })}
                                    defaultValue={field.from_origin || 0}
                                    readOnly
                                  />
                                ) : (
                                  <div className="form-control disabled">
                                    {renderCurrency(
                                      getValues(`tier.${index}.from_origin`) ||
                                        field.from_origin,
                                      false
                                    )}
                                  </div>
                                )}
                                <div className="input-group-append">
                                  <span className="input-group-text">MMK</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <div
                            className={cn("form-group mb-0 row", {
                              "form-group-error":
                                errors?.tier?.[index]?.to_origin,
                            })}
                          >
                            <label className="col-form-label col-sm-5 text-lg-right">
                              To
                            </label>
                            <div className="col-sm-7 p-0">
                              <div
                                className={cn("input-group", {
                                  "form-error":
                                    errors?.tier?.[index]?.to_origin,
                                })}
                              >
                                {mode === "EDIT" ? (
                                  <input
                                    type="text"
                                    className="form-control"
                                    {...register(`tier.${index}.to_origin`, {
                                      required: {
                                        value: selectedCurrency === "MMK",
                                        message: "Please input amount",
                                      },
                                      validate: (value) =>
                                        selectedCurrency !== "MMK" ||
                                        validateInputToOrigin(value, index),
                                    })}
                                    onChange={(e) => {
                                      handleChangeInputToOrigin(e, index);
                                    }}
                                    onKeyDown={(e) => {
                                      e.key === "Enter" && e.preventDefault();
                                    }}
                                    defaultValue={field.to_origin}
                                    disabled={mode === "VIEW"}
                                    readOnly={selectedCurrency !== "MMK"}
                                  />
                                ) : (
                                  <div className="form-control disabled">
                                    {renderCurrency(
                                      getValues(`tier.${index}.to_origin`) ||
                                        field.to_origin,
                                      false
                                    )}
                                  </div>
                                )}
                                <div className="input-group-append">
                                  <span className="input-group-text">MMK</span>
                                </div>
                              </div>
                              {errors?.tier?.[index]?.to_origin && (
                                <span className="input-error-message">
                                  {errors?.tier?.[index]?.to_origin?.message ??
                                    ""}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <div
                            className={cn("form-group mb-0 row", {
                              "form-group-error":
                                errors?.tier?.[index]?.cashback,
                            })}
                          >
                            <label className="col-form-label col-sm-5 text-lg-right">
                              Cashback
                            </label>
                            <div className="col-sm-7 p-0">
                              <div
                                className={cn("input-group", {
                                  "form-error": errors?.tier?.[index]?.cashback,
                                })}
                              >
                                {mode === "EDIT" ? (
                                  <input
                                    type="text"
                                    className="form-control"
                                    {...register(`tier.${index}.cashback`, {
                                      required: {
                                        value: true,
                                        message: "Please input amount",
                                      },
                                      validate: (value) =>
                                        validateCashback(value, index),
                                    })}
                                    onChange={(e) =>
                                      handleChangeInputCashback(e, index)
                                    }
                                    onKeyDown={(e) => {
                                      e.key === "Enter" && e.preventDefault();
                                    }}
                                    defaultValue={field.cashback}
                                    disabled={mode === "VIEW"}
                                  />
                                ) : (
                                  <div className="form-control disabled">
                                    {getValues(`tier.${index}.cashback`) ||
                                      field.cashback}
                                  </div>
                                )}
                                <div className="input-group-append">
                                  <span className="input-group-text">THB</span>
                                </div>
                              </div>
                              {errors?.tier?.[index]?.cashback && (
                                <span className="input-error-message">
                                  {errors?.tier?.[index]?.cashback?.message ??
                                    ""}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row mt-3 mx-0">
                        <div className="col-sm-4">
                          <div className="form-group mb-0 row">
                            <div className="col-sm-7 offset-md-5 p-0">
                              <div className="input-group">
                                {mode === "EDIT" ? (
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(
                                      `tier.${index}.from_destination`
                                    )}
                                    defaultValue={field.from_destination}
                                    readOnly
                                  />
                                ) : (
                                  <div className="form-control disabled">
                                    {renderCurrency(
                                      getValues(
                                        `tier.${index}.from_destination`
                                      ) || field.from_destination,
                                      true
                                    )}
                                  </div>
                                )}
                                <div className="input-group-append">
                                  <span className="input-group-text">THB</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <div
                            className={cn("form-group mb-0 row", {
                              "form-group-error":
                                errors?.tier?.[index]?.to_destination,
                            })}
                          >
                            <div className="col-sm-7 offset-md-5 p-0">
                              <div
                                className={cn("input-group", {
                                  "form-error":
                                    errors?.tier?.[index]?.to_destination,
                                })}
                              >
                                {mode === "EDIT" ? (
                                  <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    {...register(
                                      `tier.${index}.to_destination`,
                                      {
                                        required: {
                                          value: selectedCurrency === "THB",
                                          message: "Please input amount",
                                        },
                                        validate: (value) =>
                                          selectedCurrency !== "THB" ||
                                          validateInputToDestination(
                                            value,
                                            index
                                          ),
                                      }
                                    )}
                                    defaultValue={field.to_destination}
                                    disabled={mode === "VIEW"}
                                    readOnly={selectedCurrency !== "THB"}
                                    onChange={(e) =>
                                      handleChangeInputToDestination(e, index)
                                    }
                                    onKeyDown={(e) => {
                                      e.key === "Enter" && e.preventDefault();
                                    }}
                                  />
                                ) : (
                                  <div className="form-control disabled">
                                    {renderCurrency(
                                      getValues(
                                        `tier.${index}.to_destination`
                                      ) || field.to_destination,
                                      true
                                    )}
                                  </div>
                                )}
                                <div className="input-group-append">
                                  <span className="input-group-text">THB</span>
                                </div>
                              </div>
                              {errors?.tier?.[index]?.to_destination && (
                                <span className="input-error-message">
                                  {errors?.tier?.[index]?.to_destination
                                    ?.message ?? ""}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {mode === "EDIT" && fields.length < 15 && (
              <div className="text-center">
                <button className="btn btn-dashed mb-4" onClick={addTier}>
                  + Add Tier
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TierPricing;
